import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { key: 4 }

import { computed, reactive, ref, PropType, watch } from 'vue';
import { useRouter } from 'vue-router';
import { openRouterLink } from '@/utils/router-utils';
import { deepParseDates } from '@/utils/date-utils';
import {
  DataPipelineTaskTabOptionType,
  DATA_PIPELINE_TASK_TAB_OPTIONS,
} from '@/utils/compute-utils';
import { highlightJson } from '@/utils/prism-utils';
import ComputeTaskDebugDetails from '@/components/compute/ComputeTaskDebugDetails.vue';
import TaskResultState from '@/components/compute/TaskResultState.vue';
import { useProjectTaskQuery } from '@/composables/project-task-polling';
import { BucketType, ComputeTaskType, DataObjectType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BModal,
  BPagination,
  BSpinner,
} from 'bootstrap-vue-next';
import { COMPUTE_TASK_TYPES, PAGING_ITEMS_PER_PAGE } from '@/constants';
import { TaskFileType } from '@/components/compute/ComputeTaskFilesList.vue';
import NextflowTask from '@/components/compute/NextflowTask.vue';
import ComputeTaskAbort from '@/components/compute/ComputeTaskAbort.vue';
import { useUserStore } from '@/store/user';
import { getFileDownloadUrl } from '@/utils/fileService';
import DataObjectDetail from '@/components/data/DataObjectDetail.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'NextflowTasks',
  props: {
  projectId: { type: Number as PropType<number | null>, default: null },
  // paging specification
  pagingItemsPerPage: { type: Number, default: PAGING_ITEMS_PER_PAGE },
  // additinal component props
  messageNoItemsFound: {
    type: String,
    default: 'No compute tasks found.',
  },
  taskNameColumnTitle: {
    type: String,
    default: 'Task Name',
  },
  showExecDetails: {
    type: Boolean,
    default: true,
  },
},
  setup(__props, { expose: __expose }) {

const router = useRouter();
const userStore = useUserStore();

// --- Component Properties ---
const props = __props;

// --- Component State ---
const disableTransitionConfig = ref<'none' | 'multi' | 'all'>('multi'); // none, multi (multiple cards only), all (disable single card animations too)
const disableTransitionsState = ref(disableTransitionConfig.value === 'all');
const selectedTask = ref<ComputeTaskType | null>(null);
const selectedDataObject = ref<DataObjectType | null>(null);
const selectedObjectDownloadAllowed = computed(
  () => selectedDataObject.value?.isFolder === false
);
const showModalDataObjectDetail = ref(false);
const pagingPageCurrent = ref<number>(1);
const pagingTotalRows = computed(() => {
  return tasksQueryResult.value?.computeTasks?.total || 0;
});

// Tab selection for tasks & subtasks
// get helper with safe default
const selectedTabByTaskId = reactive<
  Record<number, DataPipelineTaskTabOptionType>
>({});

function getSelectedTab(id?: number | null) {
  if (!id) {
    return DATA_PIPELINE_TASK_TAB_OPTIONS.FILES;
  }
  const selectedTab = selectedTabByTaskId[id];
  if (selectedTab) {
    return selectedTab;
  }
  // otherwise check whether the task is root or subtask and set default tab accordingly
  const task = tasks.value.find((t) => t.id === id);
  if (task && task.parentId) {
    // subtask - default to progress
    return DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS;
  }

  // otherwise consider root task - default to files
  return DATA_PIPELINE_TASK_TAB_OPTIONS.FILES;
}

function setSelectedTab(
  id?: number | null,
  val: DataPipelineTaskTabOptionType = DATA_PIPELINE_TASK_TAB_OPTIONS.FILES
) {
  if (id) {
    selectedTabByTaskId[id] = val;
  }
}

// Expanded/Collapsed tasks & subtasks
// get helper with safe default
const expandedByTaskId = reactive<Record<number, boolean>>({});

function getExpanded(id?: number | null) {
  if (!id) {
    return false;
  }
  // if id not in the map, default to false unless root task with no parent and multiple subtasks
  if (expandedByTaskId[id] === undefined) {
    const task = rootTasks.value.find(
      (t) => t.id === id && t.taskType === COMPUTE_TASK_TYPES.COMPUTE_PIPELINE
    );
    if (task) {
      // root compute pipeline task - default to expanded view if files are available
      return !!(task.results.filesAvailable || task.results.files?.length);
    } else {
      // otherwise default to collapsed
      return false;
    }
  }
  // otherwise return the value
  return expandedByTaskId[id];
}

// write helper used by child event
function setExpanded(id?: number | null, val = false) {
  if (id) {
    expandedByTaskId[id] = val;
    return setTaskExpanded(id, val);
  }
}

// --- Compute Tasks List ---
const tasks = computed<ComputeTaskType[]>(() => {
  return (
    tasksQueryResult.value?.computeTasks?.computeTasks
      ?.filter((task) => task && task !== null)
      .map((task) => ({
        ...(deepParseDates(task) as ComputeTaskType),
      })) || []
  );
});

// --- Compute Task Hierarchy ---
const taskIdList = computed(() => {
  const ids: number[] = [];
  tasks.value.forEach((task) => {
    if (task && task.id) {
      ids.push(task.id);
    }
  });
  return ids;
});

const rootTasks = computed(() => {
  // find all root tasks (parentId is null)
  const roots = tasks.value.filter((task) => !task.parentId);
  // also add elements with parentId that are not in the list of root tasks
  tasks.value.forEach((task) => {
    if (task && task.parentId && !taskIdList.value.includes(task.parentId)) {
      roots.push(task);
    }
  });
  // ensure unique roots by id
  return roots;
});

// --- Component state ---

const noTasks = computed(() => {
  return tasks.value.length === 0;
});

// --- Query Compute Tasks ---
const taskFilters = computed(() => ({
  projectId: props.projectId,
  taskTypes: [COMPUTE_TASK_TYPES.NEXTFLOW_PIPELINE],
  pagingItemsPerPage: props.pagingItemsPerPage,
}));

watch(
  taskFilters,
  () => {
    pagingPageCurrent.value = 1;
  },
  { flush: 'sync' }
);

const taskQueryVariables = computed(() => ({
  ...taskFilters.value,
  pagingPageCurrent: pagingPageCurrent.value,
}));

const {
  result: tasksQueryResult,
  loading: tasksQueryInProgress,
  error: tasksQueryError,
  refetch: refetchTasks,
  onResult: onTasksQueryDone,
  onError: onTasksQueryError,
  detailErrors,
  setVisibleTasks,
  setExpanded: setTaskExpanded,
} = useProjectTaskQuery(taskQueryVariables);

watch(rootTasks, (rows) => setVisibleTasks(rows.map((task) => task.id!)), {
  immediate: true,
});

onTasksQueryDone((res) => {
  // Polling can remove the last page. Only a completed response may correct
  // it: Apollo can temporarily expose old or empty data while loading.
  if (!res.loading && typeof res.data?.computeTasks?.total === 'number') {
    const lastPage =
      props.pagingItemsPerPage > 0
        ? Math.max(
            1,
            Math.ceil(res.data.computeTasks.total / props.pagingItemsPerPage)
          )
        : 1;
    if (pagingPageCurrent.value > lastPage) {
      pagingPageCurrent.value = lastPage;
    }
  }

  if (res.data) {
    if (res.data.computeTasks) {
      console.debug('Tasks retrieved successfuly (ComputeTasksNested)');
    } else {
      console.error('Error, could not retrieve tasks:', res.data);
    }
  } else if (!res.loading) {
    console.error('Error, could not retrieve tasks result data:', res);
  }
});

onTasksQueryError((err) => {
  console.error('Error fetching code (onCodeError):', err.message);
});

function requestRefetchTasks() {
  refetchTasks()
    ?.catch((err) => {
      console.error('Refetch tasks failed:', err);
    })
    .then(() => {
      console.debug('Refetched tasks');
    });
}

function requestSearch() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchTasks();
  }
}

// --- File and Directory Requests ---
function directoryInfoRequested(task: ComputeTaskType, file: TaskFileType) {
  console.debug('directory-info-request', file);
  selectedTask.value = task;
  selectedDataObject.value = {
    isFolder: true,
    name: file.path,
  } as DataObjectType;
  showModalDataObjectDetail.value = true;
}

function directoryViewRequested(task: ComputeTaskType, file: TaskFileType) {
  if (!file.path) return;
  // The bucket browser lists virtual prefixes; a folder need not have a marker
  // object that the file-download API can sign.
  const path = file.path.endsWith('/') ? file.path : `${file.path}/`;
  bucketRequested(task.bucket, undefined, undefined, path);
}

function filePreviewRequested(task: ComputeTaskType, file: TaskFileType) {
  console.debug('file-preview-request', file);
  selectedTask.value = task;
  selectedDataObject.value = {
    isFolder: false,
    name: file.path,
  } as DataObjectType;
  showModalDataObjectDetail.value = true;
}

async function fileDownloadRequested(
  task: ComputeTaskType,
  file: TaskFileType
) {
  console.debug('file-download-request', file);
  // validate the selected data object
  if (!file.name) {
    console.error('No name found in selected data object');
    return;
  }
  if (!task?.bucket?.id) {
    console.error('Task Bucket ID not found');
    return;
  }
  // get the download URL
  const url = await getFileDownloadUrl(task.bucket.id, file.path);
  // validate the URL
  if (!url) {
    console.error(
      `Unexpected error fetching download URL for the file ${file.path}`
    );
    alert(
      'Failed to generate download link. The requested file might not be available anymore.'
    );
    return;
  }
  // open the URL in a new tab to trigger the download
  window.open(url, '_blank');
}

// --- Compute Task Abort Request ---
const showConfirmAbortModal = ref<boolean>(false);

function taskAbortRequested(task: ComputeTaskType) {
  if (!task?.id) {
    console.error('Task cancellation requested, but task ID is missing');
    return;
  }
  console.debug('Cancel task ', task.id, 'requested, waiting for confirmation');
  selectedTask.value = task;
  showConfirmAbortModal.value = true;
}

function closeTaskAbortModal() {
  showConfirmAbortModal.value = false;
}

// --- Debug Details ---
const debugTaskId = ref<number | null>(null);
const detailsContent = ref<any>(null);
const detailsModalTitle = ref('Compute Task Details');
const showDetailsModal = ref(false);

async function showExecDetails(task: ComputeTaskType) {
  if (task.id) {
    const loaded = await setExpanded(task.id, true);
    if (!loaded) return;
    task = loaded;
  }
  detailsModalTitle.value = task.name || 'Compute Task Exec Details';
  let censoredAttributes = task.attributes;

  // only allow certain attributes to be copied and viewed if not admin/developer
  if (!userStore.admin || !userStore.developer) {
    censoredAttributes = Object.fromEntries(
      Object.entries(task.attributes).filter(([key]) =>
        [
          // common attribues
          'samples', // bcl to fastq conversion + data pipeline
          'version',
          // ftp transfer
          'ftpCmd',
          'autoAnnotate',
          'reconstructData',
          'createProject',
          // data pipeline
          'startPhase',
          'endPhase',
          'bucketOutput',
        ].includes(key)
      )
    );
    // if task.attributes.samples is available, convert the array of objects to array of strings by taking the object's name property only
    if (Array.isArray(censoredAttributes.samples)) {
      censoredAttributes.samples = censoredAttributes.samples.map(
        (sample: { name: string }) => sample.name
      );
    }
  }
  detailsContent.value = censoredAttributes;
  showDetailsModal.value = true;
}

// --- Expose Properties to the Outside ---
__expose({
  tasks,
  pagingTotalRows,
  tasksQueryInProgress,
  requestSearch,
});

// --- Manage Cards Layout Changes ---
function onLayoutNeeded(phase: 'start' | 'end' | 'resize', id?: number | null) {
  console.debug('Layout requested, phase: ', phase, 'id:', id);

  // check task ID provided
  if (!id) {
    console.warn('Layout requested but no task ID provided');
    return;
  }

  // identify the container element for the task
  const container = document.querySelector(`#task-${id}`) as HTMLElement | null;

  // check container exists
  if (!container) {
    console.warn(`Layout requested but container #task-${id} not found`);
    return;
  }

  // pause transitions while heights are changing
  if (phase === 'start' && disableTransitionsState.value) {
    console.debug('Pausing transitions for layout');
    container.classList.add('task-no-anim');
  }

  // resume transitions when layout is complete
  if (phase === 'end' && disableTransitionsState.value) {
    requestAnimationFrame(() => {
      console.debug('Resuming transitions after layout');
      container.classList.remove('task-no-anim');
    });
  }
}

// --- Router Helpers for Navigation ---
function bucketRequested(
  bucket: BucketType | undefined | null,
  index?: number,
  evt?: MouseEvent,
  path?: string
) {
  if (bucket && bucket.id) {
    // redirect to bucket detail page if permitted
    if (!bucket.deletedAt) {
      openRouterLink(
        {
          name: 'BucketDetail',
          params: { id: bucket.id },
          query: {
            ...(path ? { path } : {}),
            showMetadata: '1',
            allowDownload: '1',
            listComputeTasks: '0',
            listSamples: '0',
            listProjects: '0',
            listUsers: '0',
            returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
            returnLabel: 'Project Detail',
          },
        },
        router,
        evt
      );
    } else {
      alert('The bucket for this task has been deleted.');
    }
  }
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    (_unref(tasksQueryInProgress))
      ? (_openBlock(), _createBlock(_unref(BAlert), {
          key: 0,
          "model-value": true,
          variant: "info"
        }, {
          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
            _createTextVNode(" Loading... ", -1)
          ]))]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (_unref(tasksQueryError))
      ? (_openBlock(), _createBlock(_unref(BAlert), {
          key: 1,
          "model-value": true,
          variant: "danger"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(tasksQueryError).message) + " ", 1),
            _createVNode(_unref(BButton), {
              onClick: requestRefetchTasks,
              variant: "outline-dark",
              class: "btn-sm"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_font_awesome_icon, { icon: "sync" }),
                _cache[7] || (_cache[7] = _createTextVNode(" Retry ", -1))
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_unref(BAlert), {
      "model-value": noTasks.value && !_unref(tasksQueryError),
      variant: "secondary"
    }, {
      default: _withCtx(() => [
        (_unref(tasksQueryInProgress))
          ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
              _cache[8] || (_cache[8] = _createTextVNode(" Loading tasks ", -1)),
              _createVNode(_unref(BSpinner), { small: "" })
            ]))
          : (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(props.messageNoItemsFound), 1))
      ]),
      _: 1
    }, 8, ["model-value"]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rootTasks.value, (rootTask, idx) => {
      return (_openBlock(), _createElementBlock(_Fragment, {
        key: rootTask.id || idx
      }, [
        (getExpanded(rootTask.id))
          ? (_openBlock(), _createBlock(TaskResultState, {
              key: 0,
              error: _unref(detailErrors)[rootTask.id!]
            }, null, 8, ["error"]))
          : _createCommentVNode("", true),
        _createVNode(NextflowTask, {
          id: `task-${rootTask.id}`,
          task: rootTask,
          class: "mb-2",
          expanded: getExpanded(rootTask.id),
          "selected-tab": getSelectedTab(rootTask.id),
          "onUpdate:expanded": (val) => setExpanded(rootTask.id ?? 0, val),
          "onUpdate:selectedTab": val =>
      setSelectedTab(rootTask.id!, val),
          onLayoutNeeded: onLayoutNeeded,
          onBucketRequested: ($event: any) => (bucketRequested(rootTask.bucket, idx, $event)),
          onExecDetailsRequested: ($event: any) => (showExecDetails(rootTask)),
          onDebugDetailsRequested: ($event: any) => (debugTaskId.value = rootTask.id || null),
          onAbortRequested: ($event: any) => (taskAbortRequested(rootTask)),
          onDirectoryInfoRequested: 
          (file) => directoryInfoRequested(rootTask, file)
        ,
          onDirectoryViewRequested: 
          (file) => directoryViewRequested(rootTask, file)
        ,
          onFilePreviewRequested: (file) => filePreviewRequested(rootTask, file),
          onFileDownloadRequested: 
          (file) => fileDownloadRequested(rootTask, file)
        
        }, null, 8, ["id", "task", "expanded", "selected-tab", "onUpdate:expanded", "onUpdate:selectedTab", "onBucketRequested", "onExecDetailsRequested", "onDebugDetailsRequested", "onAbortRequested", "onDirectoryInfoRequested", "onDirectoryViewRequested", "onFilePreviewRequested", "onFileDownloadRequested"])
      ], 64))
    }), 128)),
    (
        __props.pagingItemsPerPage > 0 && pagingTotalRows.value > props.pagingItemsPerPage
      )
      ? (_openBlock(), _createBlock(_unref(BPagination), {
          key: 2,
          modelValue: pagingPageCurrent.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((pagingPageCurrent).value = $event)),
          "total-rows": pagingTotalRows.value,
          "per-page": __props.pagingItemsPerPage,
          "aria-label": "Nextflow task pages",
          pills: "",
          size: "sm"
        }, null, 8, ["modelValue", "total-rows", "per-page"]))
      : _createCommentVNode("", true),
    (debugTaskId.value)
      ? (_openBlock(), _createBlock(ComputeTaskDebugDetails, {
          key: debugTaskId.value,
          "task-id": debugTaskId.value,
          "project-id": props.projectId,
          onClose: _cache[1] || (_cache[1] = ($event: any) => (debugTaskId.value = null))
        }, null, 8, ["task-id", "project-id"]))
      : _createCommentVNode("", true),
    _createVNode(_unref(BModal), {
      modelValue: showDetailsModal.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showDetailsModal).value = $event)),
      class: _normalizeClass([detailsContent.value ? 'modal-xl' : 'modal-sm']),
      onClose: _cache[4] || (_cache[4] = ($event: any) => {
        showDetailsModal.value = false;
        detailsContent.value = null;
      })
    }, {
      header: _withCtx(() => [
        _createElementVNode("h2", null, [
          _createVNode(_component_font_awesome_icon, {
            icon: "sliders",
            class: "me-2"
          }),
          _createTextVNode(" " + _toDisplayString(detailsModalTitle.value), 1)
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(BButton), {
          variant: "secondary",
          onClick: _cache[2] || (_cache[2] = ($event: any) => {
            showDetailsModal.value = false;
            detailsContent.value = null;
          })
        }, {
          default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
            _createTextVNode(" Close ", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          (detailsContent.value)
            ? (_openBlock(), _createElementBlock("pre", {
                key: 0,
                class: "code-highlight",
                innerHTML: _unref(highlightJson)(detailsContent.value)
              }, null, 8, _hoisted_3))
            : (_openBlock(), _createElementBlock("p", _hoisted_4, [
                _createVNode(_component_font_awesome_icon, { icon: "exclamation-triangle" }),
                _cache[9] || (_cache[9] = _createElementVNode("strong", null, "No Details", -1))
              ]))
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "class"]),
    (
        selectedDataObject.value &&
        selectedTask.value?.bucket?.name &&
        selectedTask.value?.bucket?.id
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(DataObjectDetail, {
            show: showModalDataObjectDetail.value,
            "data-object": selectedDataObject.value,
            "bucket-name": selectedTask.value.bucket.name,
            "bucket-id": selectedTask.value.bucket.id,
            "allow-download": selectedObjectDownloadAllowed.value,
            onClose: _cache[5] || (_cache[5] = ($event: any) => {
          showModalDataObjectDetail.value = false;
          selectedDataObject.value = null;
        })
          }, null, 8, ["show", "data-object", "bucket-name", "bucket-id", "allow-download"])
        ]))
      : _createCommentVNode("", true),
    (showConfirmAbortModal.value && selectedTask.value?.id)
      ? (_openBlock(), _createBlock(ComputeTaskAbort, {
          key: 5,
          show: showConfirmAbortModal.value,
          task: selectedTask.value,
          onClose: closeTaskAbortModal,
          onCloseAndRefresh: closeTaskAbortModal
        }, null, 8, ["show", "task"]))
      : _createCommentVNode("", true)
  ]))
}
}

})